import { FC } from 'react';
import { Chip, ChipProps } from '@mui/material';

import { ApplicationStatus, StatusColor } from '@libs/types';

interface ApplicationStatusChipProps extends Omit<ChipProps, 'color'> {
  status?: ApplicationStatus | string;
  customStatus?: string;
  statusColor?: string;
}

export const ApplicationStatusChip: FC<ApplicationStatusChipProps> = ({ 
  status, 
  customStatus, 
  statusColor,
  ...chipProps 
}) => {
  if (!status) return null;

  let label = '';
  let backgroundColor = '';
  let color = '';

  if (status === ApplicationStatus.CUSTOM && customStatus) {
    label = customStatus;
    
    // Check if statusColor is a hex value
    if (statusColor && /^#[0-9A-F]{6}$/i.test(statusColor)) {
      backgroundColor = statusColor;
      // Calculate contrast color (black or white) based on hex brightness
      const r = parseInt(statusColor.slice(1, 3), 16);
      const g = parseInt(statusColor.slice(3, 5), 16);
      const b = parseInt(statusColor.slice(5, 7), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      color = brightness > 128 ? '#000000' : '#FFFFFF';
    } else {
      // Fallback to enum values for backward compatibility
      switch (statusColor) {
        case StatusColor.GREEN:
          backgroundColor = 'success.light';
          color = 'success.contrastText';
          break;
        case StatusColor.BLUE:
          backgroundColor = 'primary.light';
          color = 'primary.contrastText';
          break;
        case StatusColor.GRAY:
          backgroundColor = 'secondary.light';
          color = 'secondary.dark';
          break;
        case StatusColor.YELLOW:
          backgroundColor = 'warning.light';
          color = 'warning.contrastText';
          break;
        case StatusColor.BLACK:
          backgroundColor = 'grey.900';
          color = '#fff';
          break;
        case StatusColor.RED:
          backgroundColor = 'error.light';
          color = 'error.contrastText';
          break;
        default:
          backgroundColor = 'secondary.light';
          color = 'secondary.dark';
      }
    }
  } else {
    label = status.charAt(0).toUpperCase() + status.slice(1);
    
    switch (status) {
      case ApplicationStatus.APPLIED:
        backgroundColor = 'primary.light';
        color = 'primary.contrastText';
        break;
      case ApplicationStatus.WAITING:
        backgroundColor = 'info.light';
        color = 'info.contrastText';
        break;
      case ApplicationStatus.REVIEWED:
        backgroundColor = 'secondary.main';
        color = 'secondary.contrastText';
        break;
      case ApplicationStatus.CONTACTED:
        backgroundColor = 'success.light';
        color = 'success.contrastText';
        break;
      case ApplicationStatus.SET:
        backgroundColor = 'success.main';
        color = 'success.contrastText';
        break;
      case ApplicationStatus.WITHDRAWN:
        backgroundColor = 'error.light';
        color = 'error.contrastText';
        break;
      default:
        backgroundColor = 'secondary.light';
        color = 'secondary.dark';
    }
  }

  return (
    <Chip 
      label={label} 
      sx={{ 
        backgroundColor, 
        color,
        '& .MuiChip-label': {
          color
        }
      }} 
      {...chipProps}
    />
  );
}; 